import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import type { Role } from '../auth/types';
import { LoadingScreen } from './LoadingScreen';

interface ProtectedRouteProps {
  /** Roles permitted here. Omit to require only that the user is signed in. */
  allow?: readonly Role[];
}

/**
 * Route guard.
 *
 * This is a *usability* gate, not a security boundary — the browser can be told
 * anything. Every protected resource is authorized again by the engine's own
 * RBAC middleware, which is what actually enforces access. This just avoids
 * showing someone a portal they cannot use.
 */
export function ProtectedRoute({ allow }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Wait for the first /auth/me to resolve, otherwise a hard refresh would
  // bounce an already-signed-in user to the login page.
  if (loading) return <LoadingScreen label="Checking your session" />;

  if (!user) {
    // Remember where they were headed so login can return them there.
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (allow && !allow.includes(user.role)) {
    return <Navigate to="/forbidden" replace />;
  }

  return <Outlet />;
}
